// Static imports
import { initializeIcons } from "@fluentui/react";
import log from "loglevel";
import React from "react";
import ReactDOM from "react-dom";
import { Measures, addMarkerToPageTimings } from "../telemetry/Measures";
import { Trace } from "../telemetry/Trace";
import { dashboard } from "../telemetry/TraceLogging";
import "./app.css";
import { requestClearCache } from "./externalApi/DashboardHostApi";
import { isFeatureEnabledInternal } from "./externalApi/VelocityApi";
import { requestHide } from "./externalApi/WindowApi";
import { getResources } from "./getResources";
import { lazyLoadModule } from "./lazyLoadModule";
import { ResourceLoader } from "./res/ResourceLoader";
import { formatString } from "./utils/stringUtilities";
import { getWidgetOrchestrator } from "./widgetRuntime/orchestrator/orchestrator";
import { WidgetModelApiFactory } from "./widgetRuntime/widgetApis/WidgetModelApiFactory";
import { WidgetVelocityApi } from "./widgetRuntime/widgetApis/WidgetVelocityApi";
import { initDashboardHostApiInternal } from "./externalApi/DashboardHostImpl";
import { initializeTelemetryProviders } from "./externalApi/TelemetryApi";
import { isTextDirectionRTL } from "./externalApi/DashboardHostApiInternal";
import { featureLookUp } from "./featureStaging-local";
import { setSignInStatus } from "./externalApi/AuthenticatorApi";
const RootContainer = React.lazy(() => import(
/* webpackChunkName: "RootContainer-Chunk" */
"./components/RootContainer/RootContainer"));
// Dynamic imports = delcare the promises here so they can be reused. Awaiting a promise multiple times has no effect
// Once resolved, the resolved object is immediately returned.
const TelemetryModule = import(
/* webpackChunkName: "Telemetry-Chunk" */
"../telemetry/Telemetry");
addMarkerToPageTimings("MEE.AppStart");
lazyLoadModule(TelemetryModule).then((Telemetry) => {
    Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
        type: "AppBootstrapStart",
    });
});
async function bootstrapApp() {
    addMarkerToPageTimings("MEE.BootstrapStart");
    const urlParams = new URLSearchParams(window.location.search);
    const language = urlParams.get("locale") || undefined;
    ResourceLoader.instance.initialize({
        languageOverride: language,
    });
    await ResourceLoader.instance.getAsync();
    await initDashboardHostApiInternal();
    await initializeTelemetryProviders();
    initializeIcons();
    window.addEventListener("keydown", (event) => {
        if (event.key === "Escape") {
            requestHide();
        }
    });
    const textDirection = isTextDirectionRTL() ? "rtl" : "ltr";
    document.dir = textDirection;
    Trace.logEvent(dashboard, "TextDirection", 0 /* verbose */, {
        textDirection: textDirection,
    });
    // Register a handler for unhandled errors that traces them to allow for diagnostic collection.
    window.addEventListener("error", (e) => {
        Measures.logEvent(dashboard, "WebAppError", 16777216 /* productAndServicePerformance */, {
            colNum: e.colno,
            error: JSON.stringify(e.error),
            filename: e.filename,
            lineNum: e.lineno,
            message: e.message,
        });
        // Still allow the default error handler to run.
        return false;
    });
    // Register a handler for unhandled rejected promises that traces them to allow for diagnostic collection.
    window.addEventListener("unhandledrejection", (e) => {
        var _a;
        Measures.logEvent(dashboard, "WebAppUnhandledRejection", 16777216 /* productAndServicePerformance */, {
            reason: e.reason,
        });
        if (((_a = e === null || e === void 0 ? void 0 : e.reason) === null || _a === void 0 ? void 0 : _a.name) === "ChunkLoadError") {
            requestClearCache();
        }
    });
    // set up logging level, see https://www.npmjs.com/package/loglevel
    const logLevel = urlParams.get("loglevel");
    if (logLevel) {
        log.setLevel(logLevel);
    }
    else {
        log.setLevel("error");
    }
    // set feature flags for in-browser testing
    // Add &Feature_XXX=true to query string to turn on feature (or turn off with 'false')
    let features = featureLookUp.keys();
    for (let feature of features) {
        const featureValue = urlParams.get(feature);
        if (featureValue) {
            featureLookUp.set(feature, featureValue === "true" ? true : false);
        }
    }
    log.info("Velocity Feature State:");
    featureLookUp.forEach((v, k) => log.info("key: " + k + " value: " + v));
    addMarkerToPageTimings("MEE.BootstrapEnd");
    //  Add any initialization steps that may return promise.
    return Promise.resolve();
}
bootstrapApp().then(async () => {
    document.title = formatString(getResources().App_Title).toString();
    // Add any VK here to api.interfaces.ts
    // Any feature that enters an alwaysDisabled/alwaysEnabled state should have its
    // entry updated here with an isEnabled value that matches.
    const features = [
        { feature: "Feature_3PAEP" },
        { feature: "Feature_3pPlatformFeeds" },
        { feature: "Feature_AEPEvolvedNotifications" },
        { feature: "Feature_AEPBadgeIdsPayload" },
        { feature: "Feature_AllowMultiInstanceWidgets" },
        { feature: "Feature_AppServiceAdaptiveCardHostApi", isEnabled: false },
        { feature: "Feature_AugmentedEntryPoint" },
        { feature: "Feature_AugmentedEntryPointBadging" },
        { feature: "Feature_AuthApiV5" },
        { feature: "Feature_AvoidRenderWhileHidden" },
        { feature: "Feature_BoardsLite" },
        { feature: "Feature_CacheWidgetRegistrations" },
        { feature: "Feature_Disable1PNotificationsOnDMADevices" },
        { feature: "Feature_DMA1POptimization" },
        { feature: "Feature_DMADisableAnnouncementsAndBadges" },
        { feature: "Feature_DMAEdgeUninstall" },
        { feature: "Feature_DMARestrictDataSharing" },
        { feature: "Feature_DMARestrictDiagnosticData" },
        { feature: "Feature_DynamicFeed", isEnabled: true },
        { feature: "Feature_ExpandedView" },
        { feature: "Feature_FeedsBackgroundTask" },
        { feature: "Feature_FeedsRotation" },
        { feature: "Feature_FullPageWebXT", isEnabled: true },
        { feature: "Feature_HeaderStorageApi" },
        { feature: "Feature_MultiAccount", isEnabled: true },
        { feature: "Feature_PinMigration" },
        { feature: "Feature_PinWidgetsFlyout" },
        { feature: "Feature_PlatformFeeds" },
        { feature: "Feature_PrefetchBackgroundTaskFeeds" },
        { feature: "Feature_PrefetchFeedContent" },
        { feature: "Feature_PrefetchPCS" },
        { feature: "Feature_ProtocolLaunchCommandAPI" },
        { feature: "Feature_ReauthRequiredUser", isEnabled: true },
        { feature: "Feature_RemoveDefaultInstances" },
        { feature: "Feature_SettingsFeedCustomization" },
        { feature: "Feature_SignedOutUser", isEnabled: true },
        { feature: "Feature_WebWidgetPrototype" },
        { feature: "Feature_WidgetHeaderUpdates" },
        { feature: "Feature_Unify1SCall" },
        { feature: "Feature_XamlWidgetPicker" },
        { feature: "Feature_PreDownloadInitializer" },
        { feature: "Feature_CTAOfflinePage" },
        { feature: "Feature_Ruby" },
        { feature: "Feature_InlineExperiment" }
    ];
    const persistedIsSignedIn = window.localStorage.getItem("isSignedIn");
    if (persistedIsSignedIn === "true" || persistedIsSignedIn === "false") {
        // Hostapp is the source of truth instead of localStorage, but we should still respect user's previous decision to sign in or out.
        // So perform a 1 time migration of signed in data from localStorage to hostapp.
        // import { setSignInStatus } from "./externalApi/AuthenticatorApi";
        setSignInStatus(persistedIsSignedIn === "true" ? 1 /* SignedIn */ : 2 /* SignedOut */);
        window.localStorage.removeItem("isSignedIn");
    }
    const velocityApi = new WidgetVelocityApi();
    if (window) {
        const getFeatureState = async (element) => {
            let isFeatureEnabled = !!element.isEnabled;
            if (element.isEnabled === undefined) {
                isFeatureEnabled = await isFeatureEnabledInternal(element.feature);
            }
            velocityApi.setFeatureState(element.feature, isFeatureEnabled);
        };
        let resultPromises = features.map(getFeatureState);
        await Promise.all(resultPromises);
        class OrchestratorFactory {
            getWidgetOrchestrator() {
                return getWidgetOrchestrator();
            }
        }
        // Construct the model factory with velocity first, then initialize async so that constructed objects
        // have velocity available
        addMarkerToPageTimings("MEE.CreateWidgetModelApiFactoryStart");
        let modelFactory = new WidgetModelApiFactory(velocityApi);
        window.__com_microsoft_dsh_widgetModelApiFactory = modelFactory;
        await modelFactory.initializeAsync(new OrchestratorFactory());
    }
    const isAugmentedEntryPointEnabled = velocityApi.isFeatureEnabled("Feature_AugmentedEntryPoint");
    const isAvoidRenderWhileHiddenEnabled = velocityApi.isFeatureEnabled("Feature_AvoidRenderWhileHidden");
    const isExpandedViewEnabled = velocityApi.isFeatureEnabled("Feature_ExpandedView");
    lazyLoadModule(TelemetryModule).then((Telemetry) => {
        Telemetry.logEvent(dashboard, "WebAppPerfMeasure", 16777216 /* productAndServicePerformance */, {
            type: "RootContainerRenderStart",
        });
    });
    ReactDOM.render(React.createElement(React.Suspense, { fallback: React.createElement("div", null) },
        React.createElement(RootContainer, { isAugmentedEntryPointEnabled: isAugmentedEntryPointEnabled, isAvoidRenderWhileHiddenEnabled: isAvoidRenderWhileHiddenEnabled, isExpandedViewEnabled: isExpandedViewEnabled })), document.getElementById("app-host"));
});
